import { Box, Grid, ListItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { memo } from 'react'

const ROW_COUNT = 12

const NormalViewRows = () => (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            height={32}
            sx={{
                flexShrink: 0,
                padding: '8px 8px 8px 20px',
                borderBottom: 1,
                borderColor: 'divider'
            }}
        >
            <Box sx={{ width: 48, height: 14, borderRadius: 0.5 }} />
            <Box sx={{ width: 56, height: 14, borderRadius: 0.5 }} />
        </Stack>
        {Array.from({ length: ROW_COUNT }).map((_, i) => (
            <ListItem key={i} dense disablePadding sx={{ px: 1, py: 0.75, flexShrink: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center" maxWidth={190}>
                        <Box sx={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0 }} />
                        <Stack spacing={0.5}>
                            <Box sx={{ width: 72, height: 14, borderRadius: 0.5 }} />
                            <Box sx={{ width: 96, height: 12, borderRadius: 0.5 }} />
                        </Stack>
                    </Stack>
                    <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                        <Box sx={{ width: 56, height: 14, borderRadius: 0.5 }} />
                        <Stack direction="row" spacing={0.5}>
                            <Box sx={{ width: 40, height: 12, borderRadius: 0.5 }} />
                            <Box sx={{ width: 44, height: 12, borderRadius: 0.5 }} />
                        </Stack>
                    </Stack>
                </Stack>
            </ListItem>
        ))}
    </Box>
)

const TableViewRows = () => (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TableContainer sx={{ flexShrink: 0, overflow: 'hidden' }}>
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow sx={{ height: 32 }}>
                        <TableCell sx={{ pl: 2, width: 120 }}>
                            <Box sx={{ width: 48, height: 14, borderRadius: 0.5 }} />
                        </TableCell>
                        <TableCell align="right">
                            <Box sx={{ width: 32, height: 14, borderRadius: 0.5, ml: 'auto' }} />
                        </TableCell>
                        <TableCell align="right">
                            <Box sx={{ width: 40, height: 14, borderRadius: 0.5, ml: 'auto' }} />
                        </TableCell>
                        <TableCell align="right">
                            <Box sx={{ width: 48, height: 14, borderRadius: 0.5, ml: 'auto' }} />
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Array.from({ length: ROW_COUNT }).map((_, i) => (
                        <TableRow key={i} sx={{ height: 40 }}>
                            <TableCell sx={{ pl: 2 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Box sx={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0 }} />
                                    <Stack spacing={0.5}>
                                        <Box sx={{ width: 72, height: 14, borderRadius: 0.5 }} />
                                        <Box sx={{ width: 96, height: 10, borderRadius: 0.5 }} />
                                    </Stack>
                                </Stack>
                            </TableCell>
                            <TableCell align="right">
                                <Box sx={{ width: 56, height: 14, borderRadius: 0.5, ml: 'auto' }} />
                            </TableCell>
                            <TableCell align="right">
                                <Box sx={{ width: 40, height: 14, borderRadius: 0.5, ml: 'auto' }} />
                            </TableCell>
                            <TableCell align="right">
                                <Box sx={{ width: 48, height: 14, borderRadius: 0.5, ml: 'auto' }} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    </Box>
)

type WatchlistSkeletonProps = {
    tableView?: boolean
}

/**
 * Structure-only placeholder for watchlist first-load shimmer.
 */
function WatchlistSkeleton({ tableView = false }: WatchlistSkeletonProps) {
    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                flex: 1,
                height: '100%',
                minHeight: 0,
                flexDirection: 'column',
                overflow: 'hidden'
            }}
        >
            <Grid container sx={{ flexShrink: 0, px: 1, py: 0.5, justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 20, height: 20, borderRadius: 0.5 }} />
                    <Box sx={{ width: 100, height: 16, borderRadius: 0.5 }} />
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Box sx={{ width: 32, height: 32, borderRadius: 0.5 }} />
                    <Box sx={{ width: 32, height: 32, borderRadius: 0.5 }} />
                </Stack>
            </Grid>
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {tableView ? <TableViewRows /> : <NormalViewRows />}
            </Box>
            <Stack
                direction="row"
                spacing={0.5}
                sx={{
                    flexShrink: 0,
                    borderTop: 1,
                    borderColor: 'divider',
                    px: 0.5,
                    py: 0.25,
                    minHeight: 36
                }}
            >
                {Array.from({ length: 4 }).map((_, i) => (
                    <Box key={i} sx={{ width: 72, height: 32, borderRadius: '4px 4px 0 0', flexShrink: 0 }} />
                ))}
                <Box sx={{ flex: 1 }} />
                <Box sx={{ width: 40, height: 34, borderRadius: 0.5, flexShrink: 0 }} />
                <Box sx={{ width: 40, height: 34, borderRadius: 0.5, flexShrink: 0 }} />
            </Stack>
        </Box>
    )
}

export default memo(WatchlistSkeleton)
